/**
 * Google Maps Loader Utility
 * 
 * Centralized utility to load Google Maps API only once
 * Prevents multiple script tags and warnings
 */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || 'AIzaSyDEH52hEspC6PQp-VMGQLYqLdEqrv9yBkg';
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-api-script';

/** Build script URL with language (map UI language follows user selection). */
function getScriptSrc(language = 'fr') {
  const lang = ['fr', 'en', 'de', 'nl'].includes(language) ? language : 'fr';
  return `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=${lang}`;
}

let loadPromise = null;
let isLoaded = false;

/**
 * Load Google Maps API script
 * @param {Object} options - Loading options
 * @param {boolean} options.requirePlaces - Whether to require Places library (default: true)
 * @param {string} options.language - Language for map UI (e.g. 'fr', 'en', 'de', 'nl'). Uses user-selected language when provided.
 * @returns {Promise<void>} Promise that resolves when Google Maps is loaded
 */
export function loadGoogleMaps(options = {}) {
  const { requirePlaces = true, language = 'fr' } = options;

  // If already loaded, return resolved promise
  if (isLoaded && window.google?.maps) {
    return Promise.resolve();
  }

  // If already loading, return the existing promise
  if (loadPromise) {
    return loadPromise;
  }

  // Check if script already exists in DOM
  let existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (existingScript) {
    // Script exists, wait for it to load
    loadPromise = new Promise((resolve, reject) => {
      if (window.google?.maps) {
        isLoaded = true;
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => {
        isLoaded = true;
        resolve();
      });

      existingScript.addEventListener('error', () => {
        loadPromise = null;
        reject(new Error('Failed to load Google Maps API'));
      });
    });

    return loadPromise;
  }

  // Create new script (language is fixed on first load)
  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = getScriptSrc(language);
    script.async = true;
    script.defer = true;

    script.onload = () => {
      isLoaded = true;
      resolve();
    };

    script.onerror = () => {
      loadPromise = null;
      isLoaded = false;
      console.error('Failed to load Google Maps API');
      reject(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Check if Google Maps is already loaded
 * @returns {boolean}
 */
export function isGoogleMapsLoaded() {
  return isLoaded && !!window.google?.maps;
}

/**
 * Get Google Maps API key
 * @returns {string}
 */
export function getGoogleMapsApiKey() {
  return GOOGLE_MAPS_API_KEY;
}
